import type { ActionView } from './action-confirmation';
import { displayFields } from './action-approval-copy';
import { STORE_READ_TOOLS } from '@/workspace/storefront/read-request';

const documentActions = new Set([
  'add_meeting_note',
  'create_skill_draft',
  'update_skill_draft',
  'create_agent',
  'update_agent',
  'set_agent_trigger',
  'run_agent',
  'save_work_result',
  // A paid, long build is always reviewed in the canvas, with its own primary label.
  'build_artifact',
  // So is a paid read of the company's shop, or checking its saved work again: what it reads or
  // checks, how long it takes, what it saves.
  ...STORE_READ_TOOLS,
  'propose_company_knowledge',
  'propose_knowledge_change',
  'suggest_page_edits',
]);

/** Presentation only: execution authority always comes from the server. */
export function actionPresentation(action: ActionView): 'inline' | 'canvas' {
  if (action.presentation === 'inline' || action.presentation === 'canvas')
    return action.presentation;
  // A connected call with its human presentation is a row of the grouped plan card in the
  // message, never its own canvas (connect-time access C17).
  if (action.decision?.connection) return 'inline';
  // One yes-or-no about an App's outside services: the row's sentence names the App, what it may do and where;
  // Details lists every service.
  if (action.tool === 'approve_app_outside_access') return 'inline';
  // One yes-or-no about what starts an App: the sentence names the App, the event and what it runs.
  if (
    [
      'set_app_event',
      'set_app_schedule',
      'set_app_data_writes',
      'set_app_connected_write',
      'confirm_app_code',
      'run_app_function_with_changes',
    ].includes(action.tool)
  )
    return 'inline';
  // One yes-or-no about an App's tables: the sentence names the App, the table, the field and the rows.
  if (action.tool === 'apply_app_table_change') return 'inline';
  const fields = displayFields(action);
  return documentActions.has(action.tool) ||
    fields.length > 2 ||
    fields.some(
      ([, value]) =>
        (value !== null && typeof value === 'object') ||
        String(value ?? '').length > 60 ||
        /[\r\n]/u.test(String(value ?? '')),
    )
    ? 'canvas'
    : 'inline';
}

export const actionUsesCanvas = (action: ActionView) => actionPresentation(action) === 'canvas';
